import { Select, SelectOption } from '@bloomfieldrobotics/canopy';
import { useLayoutEffect, useMemo } from 'react';
import { useBlocksByRanch } from '../../../services/entities/hooks/useBlocksByRanch';
import { Block } from '../../../services/entities/impl/blfApi/schemas/block';
import { Ranch } from '../../../services/entities/impl/blfApi/schemas/ranch';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { existsInItems } from './utils/existsInItems';
type BlockDropdownProps = {
  ranch: Ranch['id'] | undefined;
  value: string;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  disabled?: boolean;
  extraOptions?: SelectOption<string>[];
};

export const BlockDropdown = ({
  ranch,
  value,
  onChange,
  fullWidth = false,
  disabled = false,
  extraOptions = [],
}: BlockDropdownProps) => {
  const { t } = useTranslation();

  const { blocks, isBlocksLoading } = useBlocksByRanch(ranch, {
    skip: ranch === 'ALL_RANCHES',
  });

  const blockDropdownItems = useMemo(() => {
    const hasExtraOptions = extraOptions?.length > 0;

    if (blocks) {
      const blockOptions = blocks
        .map((block) => {
          if (existsInItems<Block>(block.id, blocks)) {
            return {
              label: block.name,
              value: block.id,
            };
          }
        })
        .filter(Boolean) as SelectOption<string>[];

      // Check if extra options are provided and append them to the block options
      if (hasExtraOptions) {
        return [...extraOptions, ...blockOptions];
      }

      return blockOptions;
    }

    return hasExtraOptions ? extraOptions : [];
  }, [blocks, extraOptions]);

  // Auto-select single items and validate selections
  useLayoutEffect(() => {
    if (blocks?.length === 1 && blocks[0]) {
      const singleBlockId = blocks[0].id;
      onChange(singleBlockId);
    }
  }, [blocks, onChange]);

  return (
    <Select
      options={blockDropdownItems}
      fullWidth={fullWidth}
      value={isBlocksLoading ? '' : value}
      onChange={onChange}
      label={t('Block')}
      loading={isBlocksLoading}
      disabled={!ranch || disabled}
    />
  );
};
